import ModalFrame from './modals/ModalFrame';
import { ThemeButton } from './ThemeWrapper';
import { outcomeLabel } from './LabJobProgress';

const STEP_LABELS = {
  validate_input: 'Checked the username',
  resolve_targets: 'Found the server',
  validate_env: 'Checked that delete is enabled',
  ssh_key: 'Read the lab SSH key',
  preflight: 'Read the local and central accounts',
  resolve_vnc: 'Read VNC and the cfg port',
  home_backup: 'Backed up the home',
  stop_user_processes: 'Stopped processes for the local account',
  userdel: 'Removed the local account',
  verify_local: 'Confirmed the local account is gone',
  home_data: 'Checked the home directory',
  stop_vnc: 'Stopped VNC',
  disable_vnc: 'Disabled VNC',
  remove_vnc_users: 'Updated the VNC user list',
  remove_firewall: 'Updated the firewall',
  archive_cfg: 'Archived the account cfg',
  local_groups: 'Checked the local group',
  central_access: 'Left central login for an administrator',
  operation_lock: 'Waited for the account lock',
  starting: 'Started',
  finished: 'Finished',
};

const BACKUP_PATH = /\/opt\/sms\/backup\/homes\/[a-z][a-z0-9_]{0,31}\.[0-9]{8}T[0-9]{6}Z\.tar\.gz/;

function stepLabel(step) {
  return STEP_LABELS[step] || step;
}

function activityText(item) {
  const detail = String(item.detail || '');
  if (item.step === 'stop_user_processes') {
    if (/^process scan failed/i.test(detail)) {
      const rest = detail.replace(/^process scan failed;\s*/i, '');
      return `Process scan failed${rest ? `: ${rest}` : ''}`;
    }
    if (item.ok === false || /^stop requested/i.test(detail)) {
      const rest = detail.replace(/^stop requested;\s*/i, '');
      return `Stop requested${rest ? `: ${rest}` : ''}`;
    }
    const rest = detail.replace(/^stopped;\s*/i, '');
    return `Stopped processes for the local account${rest ? `: ${rest}` : ''}`;
  }
  if (item.step === 'home_backup' && /not kept/i.test(detail)) {
    return 'Home backup not kept';
  }
  if ((item.step === 'stop_vnc' || item.step === 'disable_vnc') && /already stopped/i.test(detail)) {
    return `VNC already stopped: ${detail}`;
  }
  if ((item.step === 'stop_vnc' || item.step === 'disable_vnc') && /not checked|could not be verified/i.test(detail)) {
    return `VNC not checked: ${detail}`;
  }
  return `${stepLabel(item.step)}${detail ? `: ${detail}` : ''}`;
}

function backupPath(report) {
  if (report?.homeBackup) return report.homeBackup;
  const match = String(report?.message || '').match(BACKUP_PATH);
  return match ? match[0] : '';
}

function activityLines(report) {
  const activities = Array.isArray(report?.activities) ? report.activities : [];
  if (activities.length) {
    return activities.map((item) => ({
      ok: item.ok !== false,
      text: activityText(item),
    }));
  }
  const phrases = String(report?.message || '')
    .split(';')
    .map((item) => item.trim())
    .filter(Boolean);
  const lines = phrases.map((text) => ({
    ok: report?.status !== 'failed' || !/fail|not removed|could not/i.test(text),
    text,
  }));
  if (report?.step && report.step !== 'finished' && report?.detail) {
    lines.push({
      ok: report.status !== 'failed',
      text: `${stepLabel(report.step)}: ${report.detail}`,
    });
  }
  return lines;
}

export default function LabActionReport({ report, onClose }) {
  const open = report?.status === 'finished' || report?.status === 'failed';
  if (!open) return null;
  const failed = report.status === 'failed';
  const actionName =
    report.action === 'delete' ? 'Delete' : report.action === 'enable' ? 'Enable' : report.action === 'disable' ? 'Disable' : 'Server action';
  const path = backupPath(report);
  const lines = activityLines(report);
  return (
    <ModalFrame
      isOpen
      onClose={onClose}
      title={`${actionName} ${failed ? 'did not finish' : 'finished'}`}
      description={report.unixUsername || ''}
      maxWidthClass="max-w-xl"
      footer={
        <div className="mt-4 flex justify-end">
          <ThemeButton type="button" onClick={onClose}>
            Close
          </ThemeButton>
        </div>
      }
    >
      <div className="text-sm space-y-3 text-gray-800">
        {report.outcome ? <p>{outcomeLabel(report.outcome) || report.outcome}</p> : null}
        {report.action === 'delete' ? (
          <p>The local account removal does not delete the home directory. A central login with the same name stays until an administrator removes it.</p>
        ) : null}
        {path ? (
          <p>
            Home backup: <span className="font-mono text-xs break-all">{path}</span>
          </p>
        ) : null}
        {lines.length ? (
          <ul className="space-y-1">
            {lines.map((item, index) => (
              <li key={`${item.text}-${index}`} style={{ color: item.ok ? '#166534' : '#b91c1c' }}>
                {item.ok ? 'Done' : 'Failed'} — {item.text}
              </li>
            ))}
          </ul>
        ) : (
          <p>{report.message}</p>
        )}
      </div>
    </ModalFrame>
  );
}
